import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Tab Context for managing tab state across the app
const TabContext = createContext();

export const useTabContext = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }
  return context;
};

export const TabProvider = ({ children }) => {
  const [tabs, setTabs] = useState([]);
  const [currentTabId, setCurrentTabId] = useState(null);
  const [tabData, setTabData] = useState(new Map());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (window.electronAPI) {
      // Initialize with current tab
      const initialTabId = 'main';
      setTabs([{
        id: initialTabId,
        title: 'ACCTOO',
        url: '/',
        data: {}
      }]);
      setCurrentTabId(initialTabId);

      // Listen for Electron tab events
      window.electronAPI.onTabCreated((event, tab) => {
        setTabs(prev => [...prev, {
          id: tab.id,
          title: tab.title,
          url: tab.url,
          data: {}
        }]);
        setCurrentTabId(tab.id);
      });

      window.electronAPI.onTabClosed((event, data) => {
        setTabs(data.tabs.map(tab => ({
          id: tab.id,
          title: tab.title,
          url: tab.url,
          data: tabData.get(tab.id) || {}
        })));
        setCurrentTabId(data.tabs[data.currentTabIndex]?.id);
      });

      window.electronAPI.onTabSwitched((event, tab) => {
        setCurrentTabId(tab.id);
        // The tab switching is handled by the main process
        // We just need to update our local state
        console.log('🔄 Tab switched to:', tab.id, 'URL:', tab.url);
      });
    }
  }, [navigate]);

  const createTab = async (url = '/', title = 'New Tab') => {
    if (window.electronAPI) {
      const result = await window.electronAPI.createNewTab(url);
      if (result.success) {
        const newTab = {
          id: result.tab.id,
          title,
          url,
          data: {}
        };
        setTabs(prev => [...prev, newTab]);
        setCurrentTabId(result.tab.id);
        // Don't auto-navigate - let user switch manually (like browser)
        return result.tab.id;
      }
    }
    return null;
  };

  const closeTab = async (tabId) => {
    if (window.electronAPI && tabs.length > 1) {
      await window.electronAPI.closeTab(tabId);
      setTabs(prev => prev.filter(tab => tab.id !== tabId));
      if (currentTabId === tabId) {
        const remainingTabs = tabs.filter(tab => tab.id !== tabId);
        setCurrentTabId(remainingTabs[remainingTabs.length - 1]?.id);
      }
    }
  };

  const switchTab = async (tabId) => {
    if (window.electronAPI) {
      await window.electronAPI.switchTab(tabId);
      setCurrentTabId(tabId);
      // Tab switching is handled by the main process
      // The main process will load the correct URL for the tab
    }
  };

  const updateTabData = (tabId, key, value) => {
    setTabData(prev => {
      const newMap = new Map(prev);
      const tabData = newMap.get(tabId) || {};
      newMap.set(tabId, { ...tabData, [key]: value });
      return newMap;
    });

    setTabs(prev => prev.map(tab =>
      tab.id === tabId
        ? { ...tab, data: { ...tab.data, [key]: value } }
        : tab
    ));
  };

  const getTabData = (tabId, key) => {
    const data = tabData.get(tabId) || {};
    return data[key];
  };

  const updateTabTitle = (tabId, title) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId ? { ...tab, title } : tab
    ));
  };

  // Conflict detection and resolution
  const detectConflicts = (tabId, dataType, newData) => {
    const conflicts = [];
    tabs.forEach(tab => {
      if (tab.id !== tabId) {
        const tabData = getTabData(tab.id, dataType);
        if (tabData && JSON.stringify(tabData) !== JSON.stringify(newData)) {
          conflicts.push({
            tabId: tab.id,
            tabTitle: tab.title,
            dataType,
            conflictingData: tabData
          });
        }
      }
    });
    return conflicts;
  };

  const resolveConflict = (tabId, dataType, resolution) => {
    // resolution can be 'keep-current', 'use-other', 'merge'
    const currentData = getTabData(currentTabId, dataType);
    const otherData = getTabData(tabId, dataType);

    let resolvedData;
    switch (resolution) {
      case 'keep-current':
        resolvedData = currentData;
        break;
      case 'use-other':
        resolvedData = otherData;
        updateTabData(currentTabId, dataType, otherData);
        resolvedData = otherData;
        break;
      case 'merge':
        resolvedData = { ...currentData, ...otherData };
        updateTabData(currentTabId, dataType, resolvedData);
        break;
      default:
        resolvedData = currentData;
    }

    return resolvedData;
  };

  const value = {
    tabs,
    currentTabId,
    createTab,
    closeTab,
    switchTab,
    updateTabData,
    getTabData,
    updateTabTitle,
    detectConflicts,
    resolveConflict
  };

  return (
    <TabContext.Provider value={value}>
      {children}
    </TabContext.Provider>
  );
};

// Hook for using tab data with conflict detection
export const useTabData = (dataType, initialData = null) => {
  const { currentTabId, updateTabData, getTabData, detectConflicts, resolveConflict } = useTabContext();
  const [data, setData] = useState(() => getTabData(currentTabId, dataType) || initialData);
  const [conflicts, setConflicts] = useState([]);

  const updateData = (newData) => {
    updateTabData(currentTabId, dataType, newData);
    setData(newData);

    // Check for conflicts
    const detectedConflicts = detectConflicts(currentTabId, dataType, newData);
    setConflicts(detectedConflicts);
  };

  const resolveConflictHandler = (tabId, resolution) => {
    const resolvedData = resolveConflict(tabId, dataType, resolution);
    setData(resolvedData);
    setConflicts(prev => prev.filter(c => c.tabId !== tabId));
  };

  return {
    data,
    updateData,
    conflicts,
    resolveConflict: resolveConflictHandler
  };
};

export default TabProvider;

